import { createSlice, createSelector } from "@reduxjs/toolkit";
import { reset } from "../resetParameters";
import { selectBohrkronendurchmesser } from "./bohrkronendurchmesserSlice";
import {
  TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE,
  TEST_SEQUENCE_TYPE_SPECIFICATION_BY_GEOTECHNICAL_EXPERT,
} from "../../constants";

const initialState = {
  positionMicropiles: {
    numberOfPiles: Number.NaN,
  },
  positionPileheads: {
    pileheadeSource: "aus smartTITAN",
    numberOfPileheads: Number.NaN,
  },
  testSequenceType: TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE,
  positionTestLoadsForTypeDIN: {
    numberOfTests: 2,
    dispersionCoefficient: 1.25,
    testLoad: Number.NaN,
  },
  positionTestLoadsForTypeExpert: {
    numberOfTests: Number.NaN,
    testLoad: Number.NaN,
  },
  positionRequirements: {
    additionalCementConsumption: Number.NaN,
    additionalLengths: Number.NaN,
    drillingIntoRock: Number.NaN,
    drillingThroughObstacles: Number.NaN,
    downtimesEquipmentAndWorkforce: Number.NaN,
    downtimesEquipmentOnly: Number.NaN,
    disposalDrillingSlurry: Number.NaN,
    executionStatistics: true,
    additionalCementConsumptionIsChecked: true,
    additionalLengthsIsChecked: true,
    drillingIntoRockIsChecked: true,
    drillingThroughObstaclesIsChecked: true,
    downtimesEquipmentAndWorkforceIsChecked: true,
    downtimesEquipmentOnlyIsChecked: true,
    disposalDrillingSlurryIsChecked: true,
    executionStatisticsIsChecked: true,
  },
};

const lvTextSlice = createSlice({
  name: "lvText",
  initialState,
  reducers: {
    setLvTextState(state, action) {
      // probably this should be refined
      if (action.payload) {
        state.positionMicropiles =
          action.payload.positionMicropiles || initialState.positionMicropiles;
        state.positionPileheads =
          action.payload.positionPileheads || initialState.positionPileheads;
        state.testSequenceType = action.payload.testSequenceType || initialState.testSequenceType;
        state.positionTestLoadsForTypeDIN =
          action.payload.positionTestLoadsForTypeDIN || initialState.positionTestLoadsForTypeDIN;
        state.positionTestLoadsForTypeExpert =
          action.payload.positionTestLoadsForTypeExpert ||
          initialState.positionTestLoadsForTypeExpert;
        state.positionRequirements =
          action.payload.positionRequirements || initialState.positionRequirements;
      }
    },
    setForTypeDIN(state, action) {
      state.positionTestLoads.typeDIN = action.payload;
    },
    setForTypeExpert(state, action) {
      state.positionTestLoads.forTypeExpert = action.payload;
    },
    setNumberOfPiles(state, action) {
      state.positionMicropiles.numberOfPiles = action.payload;
    },
    setPileheadSource(state, action) {
      state.positionPileheads.pileheadeSource = action.payload;
    },
    setNumberOfPileheads(state, action) {
      state.positionPileheads.numberOfPileheads = action.payload;
    },
    setTestSequenceType(state, action) {
      state.positionTestLoads.testSequenceType = action.payload;
    },
    setTestSequenceTypeDINEN19971DIN1054EAPiles(state) {
      state.testSequenceType = TEST_SEQUENCE_TYPE_DIN_EN_1997_1_DIN_1054_EA_PFAEHLE;
    },
    setTestSequenceTypeSpecificationByGeotechnicalExpert(state) {
      state.testSequenceType = TEST_SEQUENCE_TYPE_SPECIFICATION_BY_GEOTECHNICAL_EXPERT;
    },
    setNumberOfTestsForTypeDIN(state, action) {
      state.positionTestLoadsForTypeDIN.numberOfTests = action.payload;
    },
    setNumberOfTestsForTypeDINByNumberOfPiles(state, action) {
      state.positionTestLoadsForTypeDIN.numberOfTests = calculateNumberOfTestsForTypeDIN(
        state.positionMicropiles.numberOfPiles
      );
    },
    setNumberOfTestsForTypeExpert(state, action) {
      state.positionTestLoadsForTypeExpert.numberOfTests = action.payload;
    },
    setDispersionCoefficient(state, action) {
      state.positionTestLoadsForTypeDIN.dispersionCoefficient = action.payload;
    },
    setPartialSafetyFactor(state, action) {
      state.positionTestLoadsForTypeDIN.partialSafetyFactor = action.payload;
    },
    setTestLoadForTypeDIN(state, action) {
      state.positionTestLoadsForTypeDIN.testLoad = action.payload;
    },
    setDispersionCoefficientByNumberOfTests(state, action) {
      state.positionTestLoadsForTypeDIN.dispersionCoefficient = calculateDispersionCoefficient(
        state.positionTestLoadsForTypeDIN.numberOfTests
      );
    },
    setTestLoadForTypeExpert(state, action) {
      state.positionTestLoadsForTypeExpert.testLoad = action.payload;
    },
    setAdditionalCementConsumption(state, action) {
      state.positionRequirements.additionalCementConsumption = action.payload;
    },
    setAdditionalLengths(state, action) {
      state.positionRequirements.additionalLengths = action.payload;
    },
    setDrillingIntoRock(state, action) {
      state.positionRequirements.drillingIntoRock = action.payload;
    },
    setDrillingThroughObstacles(state, action) {
      state.positionRequirements.drillingThroughObstacles = action.payload;
    },
    setDowntimesEquipmentAndWorkforce(state, action) {
      state.positionRequirements.downtimesEquipmentAndWorkforce = action.payload;
    },
    setDowntimesEquipmentOnly(state, action) {
      state.positionRequirements.downtimesEquipmentOnly = action.payload;
    },
    setDisposalDrillingSlurry(state, action) {
      state.positionRequirements.disposalDrillingSlurry = action.payload;
    },
    setExecutionStatistics(state, action) {
      state.positionRequirements.executionStatistics = action.payload;
    },
    setAdditionalCementConsumptionIsChecked(state, action) {
      state.positionRequirements.additionalCementConsumptionIsChecked = action.payload;
    },
    setAdditionalLengthsIsChecked(state, action) {
      state.positionRequirements.additionalLengthsIsChecked = action.payload;
    },
    setDrillingIntoRockIsChecked(state, action) {
      state.positionRequirements.drillingIntoRockIsChecked = action.payload;
    },
    setDrillingThroughObstaclesIsChecked(state, action) {
      state.positionRequirements.drillingThroughObstaclesIsChecked = action.payload;
    },
    setDowntimesEquipmentAndWorkforceIsChecked(state, action) {
      state.positionRequirements.downtimesEquipmentAndWorkforceIsChecked = action.payload;
    },
    setDowntimesEquipmentOnlyIsChecked(state, action) {
      state.positionRequirements.downtimesEquipmentOnlyIsChecked = action.payload;
    },
    setDisposalDrillingSlurryIsChecked(state, action) {
      state.positionRequirements.disposalDrillingSlurryIsChecked = action.payload;
    },
    setExecutionStatisticsIsChecked(state, action) {
      state.positionRequirements.executionStatisticsIsChecked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, (state, action) => {
      return initialState;
    });
  },
});

export const {
  setLvTextState,
  setNumberOfPiles,
  setPileheadSource,
  setNumberOfPileheads,
  setTestSequenceType,
  setTestSequenceTypeDINEN19971DIN1054EAPiles,
  setTestSequenceTypeSpecificationByGeotechnicalExpert,
  setNumberOfTestsForTypeDIN,
  setNumberOfTestsForTypeExpert,
  setDispersionCoefficient,
  setPartialSafetyFactor,
  setModelFactor,
  setTestLoadForTypeDIN,
  setTestLoadForTypeExpert,
  setAdditionalCementConsumption,
  setAdditionalLengths,
  setDrillingIntoRock,
  setDrillingThroughObstacles,
  setDowntimesEquipmentAndWorkforce,
  setDowntimesEquipmentOnly,
  setDisposalDrillingSlurry,
  setExecutionStatistics,
  setNumberOfTestsForTypeDINByNumberOfPiles,
  setDispersionCoefficientByNumberOfTests,
  setAdditionalCementConsumptionIsChecked,
  setAdditionalLengthsIsChecked,
  setDrillingIntoRockIsChecked,
  setDrillingThroughObstaclesIsChecked,
  setDowntimesEquipmentAndWorkforceIsChecked,
  setDowntimesEquipmentOnlyIsChecked,
  setDisposalDrillingSlurryIsChecked,
  setExecutionStatisticsIsChecked,
} = lvTextSlice.actions;

export default lvTextSlice.reducer;

export const selectNumberOfPiles = (state) => state.lvText.positionMicropiles.numberOfPiles;
export const selectPileheadSource = (state) => state.lvText.positionPileheads.pileheadeSource;
export const selectNumberOfPileheads = (state) => state.lvText.positionPileheads.numberOfPileheads;
export const selectTestSequenceType = (state) => state.lvText.testSequenceType;
export const selectModelFactor = () => 1.25;
export const selectNumberOfTestsForTypeDIN = (state) =>
  state.lvText.positionTestLoadsForTypeDIN.numberOfTests;
export const selectDispersionCoefficient = (state) =>
  state.lvText.positionTestLoadsForTypeDIN.dispersionCoefficient;
export const selectNumberOfTestsForTypeExpert = (state) =>
  state.lvText.positionTestLoadsForTypeExpert.numberOfTests;
export const selectTestLoadForTypeExpert = (state) =>
  state.lvText.positionTestLoadsForTypeExpert.testLoad;
export const selectAdditionalCementConsumption = (state) =>
  state.lvText.positionRequirements.additionalCementConsumption;
export const selectAdditionalLengths = (state) =>
  state.lvText.positionRequirements.additionalLengths;
export const selectDrillingIntoRock = (state) => state.lvText.positionRequirements.drillingIntoRock;
export const selectDrillingThroughObstacles = (state) =>
  state.lvText.positionRequirements.drillingThroughObstacles;
export const selectDowntimesEquipmentAndWorkforce = (state) =>
  state.lvText.positionRequirements.downtimesEquipmentAndWorkforce;
export const selectDowntimesEquipmentOnly = (state) =>
  state.lvText.positionRequirements.downtimesEquipmentOnly;
export const selectDisposalDrillingSlurry = (state) =>
  state.lvText.positionRequirements.disposalDrillingSlurry;
export const selectExecutionStatistics = (state) =>
  state.lvText.positionRequirements.executionStatistics;
export const selectAdditionalCementConsumptionIsChecked = (state) =>
  state.lvText.positionRequirements.additionalCementConsumptionIsChecked;
export const selectAdditionalLengthsIsChecked = (state) =>
  state.lvText.positionRequirements.additionalLengthsIsChecked;
export const selectDrillingIntoRockIsChecked = (state) =>
  state.lvText.positionRequirements.drillingIntoRockIsChecked;
export const selectDrillingThroughObstaclesIsChecked = (state) =>
  state.lvText.positionRequirements.drillingThroughObstaclesIsChecked;
export const selectDowntimesEquipmentAndWorkforceIsChecked = (state) =>
  state.lvText.positionRequirements.downtimesEquipmentAndWorkforceIsChecked;
export const selectDowntimesEquipmentOnlyIsChecked = (state) =>
  state.lvText.positionRequirements.downtimesEquipmentOnlyIsChecked;
export const selectDisposalDrillingSlurryIsChecked = (state) =>
  state.lvText.positionRequirements.disposalDrillingSlurryIsChecked;
export const selectExecutionStatisticsIsChecked = (state) =>
  state.lvText.positionRequirements.executionStatisticsIsChecked;

export const selectPileDiameter = () => {
  return createSelector([selectBohrkronendurchmesser], (bohrkronendurchmesser) => {
    return bohrkronendurchmesser + 20;
  });
};

const calculateNumberOfTestsForTypeDIN = (numberOfPiles) => {
  if (!numberOfPiles) {
    return 2;
  }
  return Math.max(2, Math.ceil(Number(numberOfPiles) * 0.03));
};

const calculateDispersionCoefficient = (numberOfTests) => {
  switch (Number(numberOfTests)) {
    case 1:
      return 1.35;
    case 2:
      return 1.25;
    case 3:
      return 1.15;
    case 4:
      return 1.05;
    default:
      return 1.0;
  }
};
